import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { anchorTracker, trackerElapsedMs } from '../../utils/tracker-time';
import {
  RiPlayCircleLine, RiStopCircleLine, RiPlayFill, RiTaskLine,
  RiArrowRightLine, RiTimeLine,
} from 'react-icons/ri';

function fmtDuration(ms) {
  if (!ms || ms < 0) return '0s';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function fmtTotalShort(ms) {
  if (!ms || ms < 0) return '0m';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function toLocalDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Compact live attendance tracker widget — start a task, stop the running one,
 * or switch tasks (auto-closes the previous). Embed on the dashboard so users
 * never have to open the Attendance page just to start tracking.
 */
export default function LiveAttendanceTracker({ onChange }) {
  const navigate = useNavigate();
  const [openTracker, setOpenTracker]   = useState(null);
  const [taskInput,   setTaskInput]     = useState('');
  const [now,         setNow]           = useState(Date.now());
  const [loading,     setLoading]       = useState(true);
  const [busy,        setBusy]          = useState(false);
  const [error,       setError]         = useState('');
  // Sum of every CLOSED tracker's duration for today, in ms.
  // The open tracker's elapsed is added on top when rendering, so the total ticks live.
  const [closedTodayMs, setClosedTodayMs] = useState(0);

  // Tick every second while a tracker is open so the elapsed counter updates
  useEffect(() => {
    if (!openTracker) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [openTracker]);

  const loadOpen = useCallback(() => {
    setLoading(true);
    api.get('/attendance/open')
      .then((res) => {
        const d = res.data;
        const t = d?.tracker || d?.data || (d?.id ? d : null);
        setOpenTracker(anchorTracker(t));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Fetch today's trackers and pre-compute the sum of every CLOSED entry.
  // We exclude the open one so we don't double-count its elapsed (added live in render).
  const loadTodayTotal = useCallback(() => {
    const todayStr = toLocalDate(new Date());
    api.get('/attendance/mine', { params: { date: todayStr } })
      .then((res) => {
        const d = res.data;
        const list = Array.isArray(d) ? d : d?.trackers || d?.data || [];
        const items = list.flatMap((g) => g.items || g.trackers || []);
        const totalMs = items.reduce((acc, it) => {
          if (!it.start_time || !it.end_time) return acc;
          const dur = new Date(it.end_time).getTime() - new Date(it.start_time).getTime();
          return acc + Math.max(0, dur);
        }, 0);
        setClosedTodayMs(totalMs);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadOpen();
    loadTodayTotal();
  }, [loadOpen, loadTodayTotal]);

  const handleStart = async (e) => {
    e?.preventDefault?.();
    const task = taskInput.trim();
    if (!task) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.post('/attendance/start', { task });
      const t = res.data?.tracker || res.data?.data || res.data;
      setOpenTracker(anchorTracker(t));
      setTaskInput('');
      // Starting after a "switch" auto-closes the previous tracker → refresh totals
      loadTodayTotal();
      onChange?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start tracker.');
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async () => {
    setBusy(true);
    setError('');
    try {
      await api.post('/attendance/stop');
      setOpenTracker(null);
      loadTodayTotal();
      onChange?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to stop tracker.');
    } finally {
      setBusy(false);
    }
  };

  const elapsed = trackerElapsedMs(openTracker, now);
  // Live "Total today" — closed sum + current open elapsed
  const totalTodayMs = closedTodayMs + elapsed;
  const totalTodayLabel = fmtTotalShort(totalTodayMs);

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">
      {loading ? (
        <div className="p-5 flex items-center gap-3">
          <div className="w-10 h-10 bg-[#F3F4F6] rounded-xl animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-[#F3F4F6] rounded w-1/3 animate-pulse" />
            <div className="h-3 bg-[#F3F4F6] rounded w-1/4 animate-pulse" />
          </div>
        </div>
      ) : openTracker ? (
        <div className="p-5" style={{ background: 'linear-gradient(135deg,rgba(9,132,227,0.05),rgba(213,0,249,0.05))' }}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                   style={{ background: 'linear-gradient(135deg,#0984e3,#d500f9)' }}>
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#0984e3]">Currently working on</p>
                <p className="text-[#1F2937] text-base font-bold mt-0.5 truncate">{openTracker.task}</p>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <p className="text-[#6B7280] text-xs flex items-center gap-1">
                    <RiPlayFill size={11} /> Started {fmtTime(openTracker.start_time)}
                  </p>
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0984e3] bg-white border border-[rgba(9,132,227,0.2)] px-2 py-0.5 rounded-full tabular-nums"
                    title="Total tracked time today"
                  >
                    <RiTimeLine size={10} /> Today {totalTodayLabel}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF]">Elapsed</p>
                <p className="text-[#0984e3] text-2xl font-black tabular-nums">{fmtDuration(elapsed)}</p>
              </div>
              <button
                onClick={handleStop}
                disabled={busy}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors cursor-pointer disabled:opacity-60"
              >
                {busy
                  ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  : <RiStopCircleLine size={16} />}
                Stop
              </button>
            </div>
          </div>

          <form onSubmit={handleStart} className="mt-4 pt-4 border-t border-[#F3F4F6] flex items-center gap-2 flex-wrap">
            <RiTaskLine size={14} className="text-[#9CA3AF]" />
            <input
              type="text"
              value={taskInput}
              onChange={(e) => setTaskInput(e.target.value)}
              placeholder="Switch to a new task… (auto-closes the current one)"
              className="input-field flex-1 min-w-[200px] text-sm"
            />
            <button
              type="submit"
              disabled={busy || !taskInput.trim()}
              className="btn btn-primary flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ padding: '0.55rem 1rem', fontSize: '13px' }}
            >
              <RiPlayCircleLine size={14} /> Switch
            </button>
            <button
              type="button"
              onClick={() => navigate('/portal/employee/attendance')}
              className="text-[#0984e3] text-xs font-semibold hover:underline cursor-pointer flex items-center gap-1"
            >
              View history <RiArrowRightLine size={12} />
            </button>
          </form>
          {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
        </div>
      ) : (
        <form onSubmit={handleStart} className="p-5 flex items-center gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-xl bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
            <RiTimeLine size={18} className="text-[#9CA3AF]" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF]">No tracker running</p>
              {closedTodayMs > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0984e3] bg-[rgba(9,132,227,0.06)] border border-[rgba(9,132,227,0.2)] px-2 py-0.5 rounded-full tabular-nums"
                  title="Total tracked time today"
                >
                  <RiTimeLine size={10} /> Today {totalTodayLabel}
                </span>
              )}
            </div>
            <p className="text-[#374151] text-sm font-semibold mt-0.5">Start a task to begin tracking time</p>
          </div>
          <input
            type="text"
            value={taskInput}
            onChange={(e) => setTaskInput(e.target.value)}
            placeholder="What are you working on?"
            className="input-field flex-1 min-w-[200px] text-sm"
          />
          <button
            type="submit"
            disabled={busy || !taskInput.trim()}
            className="btn btn-primary flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ padding: '0.55rem 1rem', fontSize: '13px' }}
          >
            {busy
              ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              : <RiPlayCircleLine size={14} />}
            Start
          </button>
          <button
            type="button"
            onClick={() => navigate('/portal/employee/attendance')}
            className="text-[#0984e3] text-xs font-semibold hover:underline cursor-pointer flex items-center gap-1"
          >
            View history <RiArrowRightLine size={12} />
          </button>
          {error && <p className="text-red-500 text-xs w-full">{error}</p>}
        </form>
      )}
    </div>
  );
}
